import { z } from "zod/v4";
import { CleanString } from "./cleanString.ts";

/*
 * This file contains all primitive types for use in more complex constructs.
 * Make sure that these primitives already apply reasonable restrictions.
 */

export const LdapName = z
  .string()
  .nonempty()
  .max(50)
  .regex(
    /^([a-z]+(\.ext)?|UID\d+)$/,
    "only lower case alpha characters, optionally suffixed with .ext, or UID<uidNumber>",
  );
export type LdapName = z.infer<typeof LdapName>;

export const LdapGroup = z
  .string()
  .nonempty()
  .max(100)
  .regex(/^[0-9a-zA-Z-]*$/, "only alphanumeric characters and - allowed");

export const Title = CleanString.max(20);
export const FirstName = CleanString.max(100);
export const LastName = CleanString.max(100);
export const Formality = z.enum(["formal", "informal", "semi-formal"]);
export type Formality = z.infer<typeof Formality>;
export const SalutationKind = z.enum(["male", "female"]);
export type SalutationKind = z.infer<typeof SalutationKind>;
export const CardLanguage = z.enum(["en", "de"]);
export type CardLanguage = z.infer<typeof CardLanguage>;
// Employee's preferred language - Intentionally separate from CardLanguage as they serve different purposes and may evolve different restrictions.
export const PreferredLanguage = z.enum(["en", "de"]);
export type PreferredLanguage = z.infer<typeof PreferredLanguage>;
export const Email = z.email().nonempty();
export const CompanyName = CleanString.max(150);
export const DepartmentName = CleanString.max(100);
export const Comment = CleanString.max(1_000);

export const ProjectName = CleanString.max(100);
export const EmployeeName = CleanString.max(200);

export const TimestampUtc = z
  .string()
  .nonempty()
  .length(24)
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/);

export const UidNumber = z.number().int().positive("Invalid user ID");

export const AggregateId = z
  .string()
  .nonempty()
  .max(100)
  .regex(
    /^[0-9a-zA-Z-_]*$/,
    "only alphanumeric and the following characters allowed: -_",
  );

export const CardId = AggregateId;

export const ProjectId = z
  .string()
  .length(8)
  .regex(/^[0-9a-z]*$/, "only lowercase alphanumeric characters allowed");

export const PublicCardId = z
  .string()
  .length(8)
  .regex(/^[0-9a-z]*$/, "only lowercase alphanumeric characters allowed");

export const ContactId = z
  .string()
  .nonempty()
  .max(64)
  .regex(/^[0-9a-z]*$/, "only lowercase alphanumeric characters allowed");

export const VoucherCode = z
  .string()
  .trim()
  .nonempty()
  .max(64)
  .regex(/^[0-9A-Za-z-]+$/, "only alphanumeric characters and - allowed");

export const VoucherIndex = z.number().int().positive();

export const ProjectInfobox = CleanString.max(500);
